import { Input } from '@/components/atoms/Input';
import { Wrapper } from '@/components/atoms/Wrapper';
import { HeadlessModal, HeadlessModalProps } from '@/components/templates/HeadlessModal';
import { cn } from '@/utils/cn';
import { useEffect, useState } from 'react';
import LivescoreSkeleton from '../LivescoreSkeleton/LivescoreSkeleton';
import CompetitorElement from '@/components/atoms/CompetitorElement/CompetitorElement';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Team } from '@/context/LivescoreLayoutContext';

export const FavoriteCompetitorsModalContent = ({
  allTeams,
  loading,
  header,
}: {
  allTeams: Team[];
  loading: boolean;
  header?: React.ReactNode;
}) => {
  const [filteredTeams, setFilteredTeams] = useState<Team[]>(allTeams);

  const [search, setSearch] = useState('');

  useEffect(() => {
    if (search === '') {
      setFilteredTeams(allTeams);
      return;
    }

    const filtered = allTeams.filter((team) => team.name.toLowerCase().includes(search.toLowerCase()));
    setFilteredTeams(filtered);
  }, [search, allTeams]);

  return (
    <>
      {header ? header : <p className="absolute top-4 left-4 font-bold text-xl">Dodaj nowe ulubione zespóły</p>}
      <Input
        type="text"
        placeholder="Szukaj..."
        iconLeft={<MagnifyingGlassIcon stroke="#A1A1AA" width={24} className="ml-3" />}
        id="competitor-name-search"
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        className="border-0 mb-4"
      />
      <Wrapper className="mt-2" fullWidth>
        <ul className="list-none px-0 w-full space-y-2 max-h-[388px] overflow-y-scroll customScroll">
          {loading ? (
            <LivescoreSkeleton items={6} className="!w-full relative z-50 h-[58px]" />
          ) : filteredTeams.length === 0 ? (
            <p className="text-center text-gray-400">Brak wyników</p>
          ) : (
            filteredTeams.slice(0, 32).map((team) => (
              <li key={team.id} className="pr-1">
                <CompetitorElement
                  highlight
                  icon="circle"
                  team={team}
                  className="rounded-lg border dark:border-border-400 border-border-250"
                />
              </li>
            ))
          )}
        </ul>
      </Wrapper>
    </>
  );
};

type FavoriteCompetitorsModalProps = Omit<HeadlessModalProps, 'children'> & {
  allTeams: Team[];
  loading: boolean;
};

const FavoriteCompetitorsModal = ({
  className,
  isOpen,
  allTeams,
  loading,
  ...props
}: FavoriteCompetitorsModalProps) => {
  return (
    <HeadlessModal
      className={cn('max-w-665 text-white', className)}
      isOpen={isOpen}
      containerClassName="h-screen !overflow-y-hidden"
      isLogo={false}
      {...props}
    >
      <FavoriteCompetitorsModalContent allTeams={allTeams} loading={loading} />
    </HeadlessModal>
  );
};

export default FavoriteCompetitorsModal;
